'use client'

import { cn } from '@/utilities/ui'
import { isRootPageSlug } from '@/utilities/rootPage'
import { X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useEffect, useState } from 'react'

import type { Header } from '@/payload-types'

import AboutUsIcon from '@/components/menuIcons/AboutUsIcon'
import AdditionsIcon from '@/components/menuIcons/AdditionsIcon'
import BathroomRemodelIcon from '@/components/menuIcons/BathroomRemodelIcon'
import CallIcon from '@/components/menuIcons/CallIcon'
import EmailIcon from '@/components/menuIcons/EmailIcon'
import EstimateIcon from '@/components/menuIcons/EstimateIcon'
import HomeRemodelIcon from '@/components/menuIcons/HomeRemodelIcon'
import KitchenRemodelIcon from '@/components/menuIcons/KitchenRemodelIcon'
import MagazineIcon from '@/components/menuIcons/MagazineIcon'
import MessageIcon from '@/components/menuIcons/MessageIcon'
import ProcessIcon from '@/components/menuIcons/ProcessIcon'
import ProjectsIcon from '@/components/menuIcons/ProjectsIcon'

import { HeaderLottieLogo } from './HeaderLottieLogo'
import { HeaderDropdownRow, HeaderNav } from './Nav'
import { withDefaultHeaderDropdowns } from './withDefaultHeaderDropdowns'
import PlusCircleIcon from '@/components/icons/PlusCircleIcon'
import { ContactFormModal } from '@/components/ContactFormModal.lazy'

interface HeaderClientProps {
  data: Header
}

type MobileMegaMenuItem = {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  external?: boolean
}

const MOBILE_MEGA_MENU_ITEMS: MobileMegaMenuItem[] = [
  { label: 'Kitchen Remodels', href: '/kitchen-remodels', icon: KitchenRemodelIcon },
  { label: 'Bathroom Remodels', href: '/bathroom-remodels', icon: BathroomRemodelIcon },
  { label: 'Home Remodels', href: '/home-remodels', icon: HomeRemodelIcon },
  { label: 'Additions & ADUs', href: '/accessory-dwelling-units', icon: AdditionsIcon },
  { label: 'Our Process', href: '/our-process', icon: ProcessIcon },
  { label: 'Our Projects', href: '/crafted-projects', icon: ProjectsIcon },
  { label: "Craft's Magazine", href: '/blogs', icon: MagazineIcon },
  { label: 'About Us', href: '/about-us', icon: AboutUsIcon },
  {
    label: 'Email Us',
    href: 'mailto:connect@crafthomeremodel.com',
    icon: EmailIcon,
    external: true,
  },
  { label: 'Message Us', href: '/contact-us', icon: MessageIcon },
  { label: 'Call Us', href: 'tel:+18588889139', icon: CallIcon, external: true },
  { label: 'Free Estimates', href: '/contact-us', icon: EstimateIcon },
]

/** Strip height (px) per page at min-[1440px]. Default: 20. */
const PAGE_STRIP_HEIGHTS: Record<string, number> = {
  '/contact-us':            20,
  '/about-us':              20,
  '/our-process':           20,
  '/crafted-projects':      20,
  '/blogs':                 20,
  '/kitchen-remodels':      20,
  '/bathroom-remodels':     20,
  '/home-remodels':         20,
  '/accessory-dwelling-units': 20,
}
const DEFAULT_STRIP_HEIGHT = 20

export const HeaderClient: React.FC<HeaderClientProps> = ({ data }) => {
  const pathname = usePathname()
  const lottie = data?.lottieLogo
  const lottiePath = lottie?.jsonPath?.trim() || '/logo/Home-remodeling.json'
  const showLottie = lottie?.enabled !== false && Boolean(lottiePath)
  const lottieW = typeof lottie?.width === 'number' ? lottie.width : 137
  const lottieH = typeof lottie?.height === 'number' ? lottie.height : 85
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isEstimateModalOpen, setIsEstimateModalOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [isWide, setIsWide] = useState(false)
  const headerData = withDefaultHeaderDropdowns(data)

  const isServicePage =
    !isRootPageSlug(pathname.slice(1)) && pathname.split('/').filter(Boolean).length === 1

  // Close menus when the route changes — render-phase adjustment (no effect needed).
  const [prevPathname, setPrevPathname] = useState(pathname)
  if (prevPathname !== pathname) {
    setPrevPathname(pathname)
    setOpenDropdownIndex(null)
    setIsMobileMenuOpen(false)
  }

  useEffect(() => {
    function onChange() {
      setScrolled((s) => s)
    }
    window.addEventListener('transparent-header-change', onChange)
    return () => window.removeEventListener('transparent-header-change', onChange)
  }, [])

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 38)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    function onResize() { setIsWide(window.innerWidth >= 1440) }
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Close the mobile menu and jump to the top of the new page. Without this,
  // navigating from a scrolled position (e.g. the footer) can keep the old
  // scroll offset on pages that don't force a scroll reset themselves.
  const closeMobileMenuAndScrollTop = () => {
    setIsMobileMenuOpen(false)
    const html = document.documentElement
    const prev = html.style.scrollBehavior
    html.style.scrollBehavior = 'auto' // bypass global smooth-scroll so it's instant
    window.scrollTo(0, 0)
    html.style.scrollBehavior = prev
  }

  return (
    <>
    {pathname !== '/' && (() => {
      const h = PAGE_STRIP_HEIGHTS[pathname] ?? DEFAULT_STRIP_HEIGHT
      return <div data-header-spacer className="hidden min-[1440px]:block sticky top-0 z-50 w-full bg-white" style={{ height: h }} aria-hidden />
    })()}
    <header
      data-homepage={pathname === '/' ? '' : undefined}
      data-about-us={pathname === '/about-us' ? '' : undefined}
      data-our-process={pathname === '/our-process' ? '' : undefined}
      data-crafted-projects={pathname === '/crafted-projects' ? '' : undefined}
      data-service-page={isServicePage ? '' : undefined}
      className={cn(
        'sticky top-[38px] z-50 w-full font-sans bg-white border-b border-header-border',
        'transition-[background,border,box-shadow,transform,top] duration-300',
      )}
      style={
        pathname === '/' || scrolled
          ? isWide ? { top: 0 } : undefined
          : isWide ? { top: PAGE_STRIP_HEIGHTS[pathname] ?? DEFAULT_STRIP_HEIGHT } : undefined
      }
    >
      {/* One hover zone: top bar + full-width submenu row */}
      <div className="flex flex-col relative" onMouseLeave={() => setOpenDropdownIndex(null)}>
        {/* Desktop: keep this strip above the sibling dropdown row so hover reaches every nav link (later siblings paint on top by default). */}
        <div className="w-full py-[10px] pl-[15px] pr-[20px] md:pl-[10px] md:pr-[10px] min-[1026px]:px-[2.5vw] 2xl:px-[2.6vw] min-[2560px]:px-[2.5vw] min-[1026px]:relative min-[1026px]:z-30 min-[1026px]:min-h-[105px] min-[1026px]:flex min-[1026px]:items-center">
          <div className="flex items-center justify-between min-[1026px]:hidden md:min-h-[64px]">
            <Link href="/" className="flex shrink-0 items-center no-underline">
              <Image
                src="/logo/logo.svg"
                alt="Craft Home Remodeling"
                width={137}
                height={63}
                className="w-[100px] h-[46.2px] md:w-[137px] md:h-[61px]"
              />
            </Link>

            <button
              aria-controls="mobile-mega-menu"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              className="inline-flex min-h-[44px] items-center gap-[10px] text-[32px] font-semibold text-black transition-colors duration-200 hover:text-[#009BAB]"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              type="button"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5 shrink-0" />
              ) : (
                <PlusCircleIcon className="h-[22px] w-[22px] shrink-0" />
              )}
              <span className="text-[26px] leading-none">Menu</span>
            </button>
          </div>

          <div className="hidden items-center gap-5 2xl:gap-[36px] min-[1026px]:flex min-[1026px]:w-full">
            {/* Logo */}
            <Link
              className="flex shrink-0 flex-row items-start gap-2 text-inherit no-underline"
              href="/"
            >
              <Image
                src="/logo/logo.svg"
                alt="Craft Home Remodeling"
                width={137}
                height={63}
                className="block min-[1440px]:hidden w-[137px] h-[63px]"
              />
              {showLottie ? (
                <span className="hidden min-[1440px]:block">
                  <HeaderLottieLogo height={lottieH} jsonPath={lottiePath} width={lottieW} />
                </span>
              ) : null}
            </Link>

            {/* Nav + CTA — nav items grow equally, button is fixed size */}
            <div className="flex flex-1 min-w-0 items-center gap-5">
              <HeaderNav
                data={headerData}
                setOpenDropdownIndex={setOpenDropdownIndex}
                openDropdownIndex={openDropdownIndex}
              />
              <button
                type="button"
                className="inline-flex shrink-0 items-center justify-center rounded-none bg-[#009BAB] px-6 py-3 leading-[20px] text-[20px] font-bold text-white transition-opacity hover:opacity-90"
                onClick={() => setIsEstimateModalOpen(true)}
              >
                Free Estimates
              </button>
              <ContactFormModal open={isEstimateModalOpen} onClose={() => setIsEstimateModalOpen(false)} />
            </div>
          </div>
        </div>

        <div className="hidden min-[1026px]:block">
          <HeaderDropdownRow data={headerData} openDropdownIndex={openDropdownIndex} />
        </div>

        <div
          className={cn(
            'fixed inset-0 z-[60] overflow-y-auto bg-black/60 transition-opacity duration-300 ease-out min-[1026px]:hidden min-[425px]:flex min-[425px]:items-center',
            isMobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
          id="mobile-mega-menu"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className={cn(
              'relative w-full max-h-[100dvh] overflow-y-auto overscroll-contain max-[425px]:min-h-[100dvh] bg-white transition-transform duration-300 ease-out',
              isMobileMenuOpen ? 'translate-y-0' : '-translate-y-4',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 mb-6 flex justify-center bg-white px-5 pb-4 pt-6 md:px-3">
              <Link
                className="inline-flex items-center text-inherit no-underline"
                href="/"
                onClick={closeMobileMenuAndScrollTop}
              >
                <Image src="/logo/logo.svg" alt="Craft Home Remodeling" width={136} height={63} />
              </Link>
              <button
                className="absolute right-5 top-6 p-1 md:right-3"
                onClick={() => setIsMobileMenuOpen(false)}
                type="button"
              >
                <X className="h-5 w-5 shrink-0" />
              </button>
            </div>

            <div className="px-5 pb-8 md:px-3">
              <div className="grid grid-cols-2 gap-x-2 gap-y-0 md:grid-cols-3 md:gap-5">
                {MOBILE_MEGA_MENU_ITEMS.map((item) => {
                  const Icon = item.icon

                  const content = (
                    <>
                      <Icon className="block h-12 w-12 shrink-0 transition-transform duration-200 group-hover:scale-105" />
                      <span className="text-[18px] font-medium">{item.label}</span>
                    </>
                  )

                  const itemClassName = cn(
                    'group flex min-h-[90px] flex-col items-center justify-center gap-1.5 px-2 py-2 text-center text-black no-underline',
                    'transition-all duration-200 hover:-translate-y-0.5 hover:text-[#009BAB]',
                  )

                  if (item.external) {
                    return (
                      <a
                        key={item.label}
                        className={itemClassName}
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {content}
                      </a>
                    )
                  }

                  return (
                    <Link
                      key={item.label}
                      className={itemClassName}
                      href={item.href}
                      onClick={closeMobileMenuAndScrollTop}
                    >
                      {content}
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
    </>
  )
}
